// How a document came in (dms_documents.upload_method, migration 149) and the upload it
// came with (upload_method_at — one time for every document of the same drop / pick).
// Process Docs sorts by it so the documents of one way of uploading, or one upload, can be
// found and fixed together: delete and upload again, move, or combine.

export type UploadMethod =
  | 'upload-single' | 'upload-separate' | 'upload-auto'
  | 'email-upload-single' | 'email-upload-separate' | 'email-upload-auto'
  | 'email-forward' | 'convert-csv' | 'payroll' | 'split' | 'combine';

/** Long label (batch headings, tooltips) and short label (the badge on a list row). */
export const UPLOAD_METHODS: Record<UploadMethod, { label: string; short: string }> = {
  'upload-single':         { label: 'Upload Receipts / Docs (file kept whole)',   short: 'Upload' },
  'upload-separate':       { label: 'Separate & Upload (one document per page)',  short: 'Separated' },
  'upload-auto':           { label: 'Auto-Separate Mixed Receipts / Docs',         short: 'Auto-separated' },
  'email-upload-single':   { label: 'Email file uploaded (.eml / .msg)',           short: 'Email file' },
  'email-upload-separate': { label: 'Email file uploaded, pages separated',        short: 'Email file · separated' },
  'email-upload-auto':     { label: 'Email file uploaded, auto-separated',         short: 'Email file · auto' },
  'email-forward':         { label: 'Forwarded email (file-by-email address)',     short: 'Forwarded email' },
  'convert-csv':           { label: 'Bank statement (Convert to CSV)',             short: 'Bank statement' },
  'payroll':               { label: 'Made by Payroll',                             short: 'Payroll' },
  'split':                 { label: 'Separated in Process Docs',                   short: 'Split here' },
  'combine':               { label: 'Combined in Process Docs',                    short: 'Combined' },
};

const UNKNOWN = { label: 'Upload method not recorded', short: 'Unknown' };

/** Order of the methods in the grouped list (as on the Upload Docs tabs). */
const METHOD_ORDER: string[] = Object.keys(UPLOAD_METHODS);

export function uploadMethodLabel(method: string | null | undefined): string {
  return (UPLOAD_METHODS as Record<string, { label: string }>)[method || '']?.label || UNKNOWN.label;
}
export function uploadMethodShort(method: string | null | undefined): string {
  return (UPLOAD_METHODS as Record<string, { short: string }>)[method || '']?.short || UNKNOWN.short;
}

/** The method for an Upload Docs tab; `email` = an .eml / .msg file dropped there. */
export function methodForUploadMode(mode: string, email = false): UploadMethod {
  const kind = mode === 'receipts-separate' ? 'separate' : mode === 'receipts-auto' ? 'auto' : 'single';
  return `${email ? 'email-upload' : 'upload'}-${kind}` as UploadMethod;
}

/** The upload a document belongs to: same method + same upload time. */
export function uploadBatchKey(d: { upload_method?: string | null; upload_method_at?: string | null }): string {
  return `${d.upload_method || ''}|${String(d.upload_method_at || '').slice(0, 19)}`;
}

const ts = (s?: string | null) => { if (!s) return 0; const t = Date.parse(String(s).replace(' ', 'T')); return isNaN(t) ? 0 : t; };

/** Place of a method in the grouped list: tab order, not recorded last. */
export function uploadMethodRank(method: string | null | undefined): number {
  const i = METHOD_ORDER.indexOf(method || '');
  return i < 0 ? METHOD_ORDER.length : i;
}

/** Sort for "Upload time": newest upload first → within an upload, the order the
 *  documents were saved. */
export function compareByUploadTime<T extends {
  upload_method_at?: string | null; created_at?: string | null; document_id?: number;
}>(a: T, b: T): number {
  const t = ts(b.upload_method_at) - ts(a.upload_method_at);
  if (t !== 0) return t;
  return (ts(a.created_at) - ts(b.created_at)) || (Number(a.document_id || 0) - Number(b.document_id || 0));
}

/** Method (tab order, unknown last) → newest upload → save order. */
export function compareByUploadMethod<T extends {
  upload_method?: string | null; upload_method_at?: string | null; created_at?: string | null; document_id?: number;
}>(a: T, b: T): number {
  return (uploadMethodRank(a.upload_method) - uploadMethodRank(b.upload_method)) || compareByUploadTime(a, b);
}

/** "26 Oct 2026, 14:03" in the viewer's own time (stored in UTC). '' when not known. */
export function formatUploadTime(at: string | null | undefined): string {
  if (!at) return '';
  const s = String(at).replace(' ', 'T');
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : s + 'Z');
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
